import { Link, useParams } from "react-router-dom";
import { categories, categoryMap } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";

export default function CategoryPage() {
  const { slug = "blood" } = useParams<{ slug: string }>();
  const category = categoryMap[slug] ?? categories[0];
  const list = getArticlesByCategory(category.slug);

  useSeo({
    title: `${category.name}漫画人气榜 | 高分${category.name}漫画推荐 - 漫天堂`,
    description: `${category.name}漫画人气榜，实时更新高分${category.name}漫画与热度排行。${category.description} 看${category.name}漫画推荐，就到漫天堂。`,
    keywords: `漫天堂,${category.name}漫画,${category.name}漫画人气榜,${category.name}漫画推荐,漫天堂${category.name}榜单,高分${category.name}漫画`,
    canonicalPath: `/category/${category.slug}`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://bestmanga.github.io/" },
          {
            "@type": "ListItem",
            position: 2,
            name: `${category.name}漫画`,
            item: `https://bestmanga.github.io/category/${category.slug}`,
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${category.name}漫画人气榜`,
        url: `https://bestmanga.github.io/category/${category.slug}`,
        inLanguage: "zh-CN",
        description: category.description,
        mainEntity: {
          "@type": "ItemList",
          itemListElement: list.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: a.title,
            url: `https://bestmanga.github.io/article/${a.slug}`,
          })),
        },
      },
    ],
  });

  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={category.banner}
            alt={`${category.name}漫画分类横幅`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/20"></div>
        </div>
        <div className="relative mx-auto flex w-full max-w-7xl flex-col px-4 py-14 md:px-6 md:py-20">
          <Breadcrumb
            items={[
              { label: "分类" },
              { label: `${category.name}漫画` },
            ]}
          />
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
              <i className={category.icon}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
                {category.name}漫画人气榜
              </h1>
              <p className="mt-1 text-sm text-background-100/90">{category.tagline}</p>
            </div>
          </div>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            {category.description}
          </p>
          <p className="mt-4 text-sm text-accent-300">
            共收录 <strong className="font-semibold">{list.length}</strong> 部人气作品，点击卡片即可查看详情
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((c) => {
            const active = c.slug === category.slug;
            return (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-foreground-950"
                }`}
              >
                <i className={c.icon}></i>
                {c.name}漫画
              </Link>
            );
          })}
        </div>

        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground-950">
            {category.name}漫画 · 全部作品
          </h2>
          <span className="text-sm text-foreground-500">{list.length} 部</span>
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {list.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-10 text-center text-sm text-foreground-600">
            该分类暂无作品，敬请期待。
          </div>
        )}
      </section>
    </div>
  );
}